// ABOUTME: Reusable button component to redirect users from old gnowbe-web to new gnowbe-frontend
// ABOUTME: Handles URL mapping to preserve user context when switching apps

import React from 'react'
import { Button } from 'components/utils/Button'
import { trackButtonEnhanced } from 'utils/track'

interface ExploreNewAppButtonProps {
  /** The screen/page name where the button is displayed (for analytics) */
  onScreen?: string
  /** Button type (default: 'gnowbe') */
  type?: 'gnowbe' | 'sub' | 'primary' | 'text' | 'guide' | 'danger' | 'team' | 'quote' | 'edit' | 'chat' | 'ad' | 'magic'
  /** Custom button text (default: 'Explore the New Web App') */
  text?: string
  /** Additional CSS classes */
  className?: string
  /**
   * The group (companyId) the learner is currently studying under. Handed to the
   * new app as `?companyId=…` so the switch keeps the learner's scope — see the
   * note on handleClick for why this matters.
   */
  companyId?: string
}

// Company ids are 17-char alphanumerics. Guard the value we emit: a component that
// renders before its data resolves can stringify `undefined`, and `?companyId=undefined`
// on the receiving side would be persisted as a real group and then sent on every
// study/progress write. The new app rejects malformed values too, but emitting a
// clean URL keeps the failure from ever leaving this app.
const COMPANY_ID_SHAPE = /^[a-z0-9]{10,30}$/i

/**
 * Path prefixes worth carrying the group on — the ones the new app rewrites to a
 * program route (`/programs/{courseId}/…`) and can therefore key the group to:
 *
 *   /learn/*    → /programs/{courseId}/sessions/…   (course from the user profile)
 *   /details/*  → /programs/{courseId}
 *
 * Everywhere else the param is not just useless, it is harmful. The new app applies
 * the group only when the rewritten path contains a courseId, and it cleans the param
 * out of the address bar *as part of applying it*. So on a non-program page (Home is
 * the common one — `/home` rewrites to `/`) the param is never consumed and therefore
 * never cleaned: a group id would simply sit in the URL, ready to be copied into a
 * shared link.
 *
 * `/edit/{companyId}-{courseId}` and `/activity/{companyId}/{courseId}` already carry
 * the company in the path, so they need nothing here.
 *
 * On Home the value would be meaningless anyway: it comes from
 * `currentCompanyIdCourseId`, i.e. the group of the last course opened, not anything
 * about the page being viewed.
 */
const PROGRAM_SCOPED_PATH_PREFIXES = ['/learn', '/details']

const isProgramScopedPath = (pathname: string): boolean =>
  PROGRAM_SCOPED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )

/**
 * Unregisters every service worker for this origin and drops all Cache Storage
 * buckets. Best effort only — every failure is swallowed so the app flip is
 * never blocked by cleanup.
 */
async function evictServiceWorkers(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((r) => r.unregister()))
    }
    // Drop image-cache-v* too: entries are keyed by path and matched with
    // `ignoreSearch: true`, so stale bytes can be served for URLs the new app
    // requests with different signatures.
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch {
    // Never block the flip on cleanup failure.
  }
}

/**
 * Button that switches a user from the old gnowbe-web app to the new gnowbe-frontend app
 * while preserving their current context/view.
 *
 * When clicked:
 * 1. Tracks an analytics event
 * 2. Sets app_version=new (2 years) — CloudFront reads this cookie to decide which
 *    bundle to serve, so the switch is a cookie write plus a reload of the SAME url
 * 3. Appends `?companyId=…` when we know the learner's current group
 * 4. Evicts this app's service worker + caches (see evictServiceWorkers)
 * 5. Reloads, so the edge serves the new app for the current path
 *
 * Why step 3 exists: the new app's routes are program-scoped only
 * (/programs/:programId/sessions/:sid/actions/:aid) and carry no group. Because this
 * button reloads the current url in place, a learner enrolled in the same course
 * through SEVERAL groups arrives with no way for the new app to know which one they
 * were in. The new app then falls back to the eventually-consistent
 * profile.currentCompanyIdCourseId — which can be stale, or point at a different
 * course entirely — and study/progress writes go out unscoped or under the wrong
 * group. Carrying the group explicitly removes the guess.
 *
 * The new app ignores the param when it is absent or malformed, so this is safe to
 * ship independently and old links keep working.
 */
export const ExploreNewAppButton: React.FC<ExploreNewAppButtonProps> = ({
  onScreen = 'Unknown',
  type = 'gnowbe',
  text = 'Try Our New Beta App',
  className,
  companyId,
}) => {
  const handleClick = async () => {
    // Track button click for analytics
    trackButtonEnhanced({
      button: text,
      onScreen,
    })

    // Set cookie app_version=new for 2 years. This goes FIRST — if the eviction
    // below hangs or throws, the flip must still land on the new app.
    const date = new Date()
    date.setTime(date.getTime() + (2 * 365 * 24 * 60 * 60 * 1000))
    document.cookie = `app_version=new; expires=${date.toUTCString()}; path=/`

    // Carry the current group across the handoff, but ONLY from a page the new app
    // can key it to (see isProgramScopedPath). Rewrite the address bar first so the
    // reload re-requests the url WITH the param (mirrors how the new app builds the
    // legacy url on the way back). Synchronous, so it lands before the awaited
    // eviction below can be interrupted.
    if (companyId && COMPANY_ID_SHAPE.test(companyId)) {
      try {
        const url = new URL(window.location.href)
        if (isProgramScopedPath(url.pathname)) {
          url.searchParams.set('companyId', companyId)
          window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
        }
      } catch {
        // Never block the switch on url handling — fall through to a plain reload.
      }
    }

    // Evict this app's service worker + caches before the reload.
    //
    // A plain reload does NOT release SW control of the document, so without
    // this the new app boots with sw.js still intercepting every cdn1.* request.
    // sw.js re-fetches those with `credentials: 'omit'`, which strips the
    // CloudFront signed cookies the new app authenticates media with → 403 on
    // every program image. unregister() only stops FUTURE documents from being
    // controlled, so it has to happen before the navigation, not after.
    await Promise.race([
      evictServiceWorkers(),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ])

    window.location.reload()
  }

  return (
    <Button
      type={type}
      text={text}
      onClick={handleClick}
      className={className}
    />
  )
}
